import {
    PublicPriorityReferralAction,
    useUpdatePublicPriorityReferralMutation
} from "@therapprove/common";
import { useMemo, useState } from "react";
import { Dialog, DialogProps } from "../components/Dialog";
import { referralLandingBase64ToPayload } from "../utils/payload.utils";

export const ReferralLanding = () => {
    // Get the 'key' query string parameter from the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get("key");

    // Mutation to accept or decline the referral
    const [, updatePriorityReferral] =
        useUpdatePublicPriorityReferralMutation();

    const [dialog, setDialog] = useState<DialogProps>({
        title: "",
        description: "",
        icon: "",
        buttons: [],
        isLoading: false,
        showAgreement: false,
        successMessage: "",
        failureMessage: "",
        isSuccessful: null
    });

    const getAcceptDeclineDialogState = (
        therapyCategoryName: string
    ): Partial<DialogProps> => ({
        title: "You've been invited",
        description: `Based on your child's needs, your information can be shared with one or more providers who may be a fit for ${therapyCategoryName}. If you accept, we'll securely send your information so they can review and reach out directly.`,
        icon: "heart_check",
        showAgreement: true,
        buttons: [
            {
                text: "Decline",
                referralAction:
                    PublicPriorityReferralAction.PriorityReferralForCaregiversDecline
            },
            {
                text: "Accept",
                variant: "primary",
                referralAction:
                    PublicPriorityReferralAction.PriorityReferralForCaregiversAccept
            }
        ]
    });

    // Use useMemo for the payload parsing.
    const decodedKey = useMemo(() => {
        if (!key) return undefined;
        try {
            const pl = referralLandingBase64ToPayload(key);
            if (!pl) {
                setDialog({
                    title: "Sorry, that link is invalid, and we couldn't process it successfully.",
                    description:
                        "Please try again, or contact support@therapprove.com if you believe this is an error.",
                    icon: "undo",
                    showAgreement: false,
                    isSuccessful: false
                });
                return undefined;
            }

            const { therapyCategoryName } = pl;
            const acceptDeclineDialogState =
                getAcceptDeclineDialogState(therapyCategoryName);

            setDialog((previous) => ({
                ...previous,
                title: "You've received an invite",
                description: `Someone on your care team has invited you to connect with other therapy providers for ${therapyCategoryName}. Take a moment to review the details and decide what feels right for your family.`,
                icon: "mail",
                showAgreement: false,
                buttons: [
                    {
                        text: "View Invite",
                        variant: "primary",
                        onClick: () => {
                            setDialog((current) => ({
                                ...current,
                                ...acceptDeclineDialogState,
                                buttons: acceptDeclineDialogState.buttons ?? []
                            }));
                        }
                    }
                ]
            }));

            return pl;
        } catch (error) {
            return undefined;
        }
    }, [key]);

    const handleSubmit = async (action: PublicPriorityReferralAction) => {
        if (!decodedKey) return;

        const response = await updatePriorityReferral({
            input: {
                priorityReferralAction: action,
                key1: decodedKey.key1,
                key2: decodedKey.key2
            }
        });

        if (
            response.error ||
            !response.data?.priorityReferrals?.updatePriorityReferral?.success
        ) {
            setDialog({
                ...dialog,
                isSuccessful: false,
                failureMessage:
                    "Sorry, an error has occurred. Please try again, or contact support@therapprove.com if the issue persists."
            });
        } else {
            // Set the success message based on the action taken.
            switch (action) {
                case PublicPriorityReferralAction.PriorityReferralForCaregiversAccept:
                    setDialog({
                        title: `You have accepted this referral.`,
                        description: `If you did not mean to accept, you can close this window, and click the link in your email to restart the process.`,
                        icon: "check",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage: `Referral accepted! ${decodedKey.patientName} has been added to the referral list for the organizations ${decodedKey.organizationName} selected.`,
                        buttons: []
                    });
                    break;
                case PublicPriorityReferralAction.PriorityReferralForCaregiversDecline:
                    setDialog({
                        title: `You have declined this referral.`,
                        description: `If you did not mean to decline, you can close this window, and click the link in your email to restart the process.`,
                        icon: "block",
                        showAgreement: false,
                        isSuccessful: true,
                        successMessage: `You have declined the referral from ${decodedKey.organizationName}. ${decodedKey.patientName} will not appear on any referral lists for this referral.`,
                        buttons: []
                    });
                    break;
                default:
                    setDialog({
                        title: "An error has occurred.",
                        description:
                            "Please try again, or contact support@therapprove.com if the issue persists.",
                        icon: "error",
                        showAgreement: false,
                        isSuccessful: false,
                        successMessage:
                            "Sorry, an error has occurred. Please try again, or contact support@therapprove.com if the issue persists.",
                        buttons: []
                    });
                    break;
            }
        }
    };

    return <Dialog {...dialog} onReferralClick={handleSubmit} />;
};
